<template>
    <BookingFormLayout
        v-bind="
            bookingFormSharedData
                .setContainerClasses('max-w-4xl')
                .getBookingFormLayoutProps()
        "
    >
        <template #content>
            <SchedulingMeetingLoadingOverlay :show="isProcessingPayment" />

            <SchedulingErrorMessage
                v-if="errorMessage"
                :errorMessage="errorMessage"
            />

            <BorderSeparatedSections
                mainContainerClasses="grid grid-cols-8"
                leftContainerClasses="col-span-12 lg:col-span-3"
                rightContainerClasses="col-span-12 lg:col-span-5"
            >
                <template #left>
                    <MeetingSelectedDetails
                        :company="localCompany"
                        :service="localServiceAggregate.service"
                        :user="localCompanyUserAggregate.user"
                        :enabledLocationTypes="
                            localServiceAggregate.getUniqueEnabledMeetingLocationTypes()
                        "
                        :selectedTimeZone="
                            localDraftMeeting.selectedTimeZone ?? undefined
                        "
                        :meetingLocationIconSize="20"
                        :recurrenceSettings="
                            localServiceAggregate.service.recurrenceSettings
                        "
                        :meetingDates="previewMeetingDates"
                        :localizer="localizer"
                    />
                </template>
                <template #right>
                    <div class="space-y-4">
                        <div>
                            <ElementWithIdOnMobile :id="DETAILS_SECTION_ID" />
                            <BookingFormTitle>
                                {{
                                    localizer.translate(
                                        TextTokenEnum.BookingForm_ConfirmMeetingDetails,
                                    )
                                }}
                            </BookingFormTitle>
                        </div>

                        <ApiForm
                            method="POST"
                            :url="scheduleMeetingUrl"
                            ref="scheduleMeetingApiFormRef"
                            :disableCatch="false"
                        >
                            <div class="space-y-8">
                                <div>
                                    <MeetingDetailsForm
                                        :draftMeeting="localDraftMeeting"
                                        :service="localServiceAggregate.service"
                                        :authCustomer="authCustomer"
                                        :googleSocialLoginEnabled="
                                            bookingFormSharedData.googleSocialLoginEnabled
                                        "
                                        :currentUrl="
                                            bookingFormSharedData.currentUrl
                                        "
                                        :recurrenceSettings="
                                            localServiceAggregate.service
                                                .recurrenceSettings
                                        "
                                        :numberOfRequestedSeats="
                                            localNumberOfRequestedSeats
                                        "
                                        @onNumberOfRecurringMeetingsToScheduleUpdated="
                                            handleNumberOfRecurringMeetingsToScheduleUpdated(
                                                $event,
                                            )
                                        "
                                        @onNumberOfRequestedSeatsUpdated="
                                            handleNumberOfRequestedSeatsUpdated(
                                                $event,
                                            )
                                        "
                                        :localizer="localizer"
                                    />
                                    <MeetingLocationForm
                                        :draftMeeting="localDraftMeeting"
                                        :serviceAggregate="
                                            localServiceAggregate
                                        "
                                        :localizer="localizer"
                                    />
                                </div>

                                <div>
                                    <SectionSubtitle
                                        v-if="
                                            localServiceAggregate.shouldRenderCustomFieldsSection()
                                        "
                                    >
                                        {{
                                            localizer.translate(
                                                TextTokenEnum.BookingForm_Questions,
                                            )
                                        }}
                                    </SectionSubtitle>
                                    <MeetingCustomFieldsForm
                                        :customFields="
                                            localServiceAggregate.customFields
                                        "
                                        :draftMeeting="localDraftMeeting"
                                        :errors="
                                            scheduleMeetingApiFormRef?.response?.getErrors() ??
                                            new ApiFormErrors({})
                                        "
                                    />
                                </div>

                                <div>
                                    <SectionSubtitle
                                        v-if="
                                            localServiceAggregate.shouldRenderPaymentSection()
                                        "
                                    >
                                        {{
                                            localizer.translate(
                                                TextTokenEnum.BookingForm_PaymentDetails,
                                            )
                                        }}
                                    </SectionSubtitle>
                                    <MeetingConfirmationForm
                                        :company="localCompany"
                                        :draftMeeting="localDraftMeeting"
                                        :serviceAggregate="
                                            localServiceAggregate
                                        "
                                        :customerPaymentMethods="
                                            localCustomerPaymentMethods
                                        "
                                        :turnstileEnabled="turnstileEnabled"
                                        :turnstileSiteKey="turnstileSiteKey"
                                        :stripePublicKey="stripePublicKey"
                                        :payPalClientId="payPalClientId"
                                        :hasSavePaymentMethodFeatureFlag="
                                            hasSavePaymentMethodFeatureFlag
                                        "
                                        :isProcessingPayment="
                                            isProcessingPayment
                                        "
                                        :numberOfRecurringMeetingsToSchedule="
                                            localNumberOfRecurringMeetingsToSchedule
                                        "
                                        :numberOfRequestedSeats="
                                            localNumberOfRequestedSeats
                                        "
                                        :localizer="localizer"
                                        :canScheduleMeeting="canScheduleMeeting"
                                        @onStart="
                                            handleConfirmationStarted($event)
                                        "
                                        @onError="
                                            handleConfirmationFailed($event)
                                        "
                                        @onScheduleMeetingRequested="
                                            handleScheduleMeetingRequested(
                                                $event,
                                            )
                                        "
                                    />
                                </div>
                            </div>

                            <input
                                class="optional"
                                type="text"
                                name="company"
                                value=""
                            />
                        </ApiForm>
                    </div>
                </template>
            </BorderSeparatedSections>
        </template>
    </BookingFormLayout>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue'
    import Company from '@/App/Company/Entity/Company'
    import CustomerPaymentMethod from '@/App/CustomerPaymentMethod/Entity/CustomerPaymentMethod'
    import DraftMeeting from '@/App/Booking/Draft/Entity/DraftMeeting'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import RecurringDate from '@/App/Booking/Recurring/Entity/RecurringDate'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import BookingFormLayout from '@/Shared/Layout/BookingForm/BookingFormLayout.vue'
    import MeetingSelectedDetails from '@/BookingForm/BookingForm/Page/Step/MeetingSelectedDetails.vue'
    import SchedulingMeetingLoadingOverlay from '@/BookingForm/BookingForm/Component/SchedulingMeetingLoadingOverlay.vue'
    import MeetingCustomFieldsForm from '@/BookingForm/BookingForm/Component/MeetingCustomFieldsForm.vue'
    import MeetingDetailsForm from '@/BookingForm/BookingForm/Component/MeetingDetailsForm.vue'
    import MeetingLocationForm from '@/BookingForm/BookingForm/Component/MeetingLocationForm.vue'
    import MeetingConfirmationForm from '@/BookingForm/BookingForm/Component/MeetingConfirmationForm.vue'
    import type {
        ApiFormRefType,
        ConfirmBookMeetingOnErrorCallbackParams,
        ConfirmBookMeetingOnScheduleMeetingRequested,
        ConfirmBookMeetingOnStartCallbackParams,
    } from '@/Shared/AutoGenerated/Types'
    import ApiFormErrors from '@/Shared/Component/Forms/ApiFormErrors'
    import ApiFormResponseType from '@/Shared/Component/Forms/ApiFormResponseType'
    import BookingFormSharedData from '@/Shared/Entity/BookingFormSharedData'
    import usePageScroll from '@/Shared/Hook/usePageScroll'
    import BorderSeparatedSections from '@/Shared/Layout/BookingForm/BorderSeparatedSections.vue'
    import SchedulingErrorMessage from '@/BookingForm/BookingForm/Component/SchedulingErrorMessage.vue'
    import SectionSubtitle from '@/BookingForm/BookingForm/Component/SectionSubtitle.vue'
    import BookingFormTitle from '@/Shared/Component/BookingFormTitle.vue'
    import ElementWithIdOnMobile from '@/BookingForm/BookingForm/Component/ElementWithIdOnMobile.vue'
    import LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
    import BookingConfirmationSubmitUrlFactory from '@/BookingForm/BookingForm/Application/Service/BookingConfirmationSubmitUrlFactory'

    interface ShowBookingFormConfirmationStepPageProps {
        bookingFormSharedDataData: any
        draftMeetingData: any
        companyUserAggregateData: any
        serviceAggregateData: any
        companyData: any
        customerPaymentMethodsData: any
        stripePublicKey: string
        payPalClientId: string
        turnstileEnabled: boolean
        turnstileSiteKey: string
        hasSavePaymentMethodFeatureFlag: boolean
        previewMeetingDatesData: any
    }

    const props = defineProps<ShowBookingFormConfirmationStepPageProps>()
    const bookingFormSharedData = BookingFormSharedData.hydrate(
        props.bookingFormSharedDataData,
    )
    const authCustomer = bookingFormSharedData.authCustomer
    const localCompanyUserAggregate = ref<CompanyUserAggregate>(
        CompanyUserAggregate.hydrateSingle(props.companyUserAggregateData),
    )
    const localServiceAggregate = ref<ServiceAggregate>(
        ServiceAggregate.hydrateSingle(props.serviceAggregateData),
    )
    const localCompany = ref<Company>(Company.hydrateSingle(props.companyData))
    const localDraftMeeting = ref<DraftMeeting>(
        DraftMeeting.hydrateSingle(props.draftMeetingData),
    )
    const localCustomerPaymentMethods = ref<CustomerPaymentMethod[]>(
        CustomerPaymentMethod.hydrateMultiple(props.customerPaymentMethodsData),
    )
    const previewMeetingDates = RecurringDate.hydrateMultiple(
        props.previewMeetingDatesData,
    )
    const localizer = LocalizerService.make(bookingFormSharedData.currentLocale)

    const errorMessage = ref<string | null>(null)
    const isProcessingPayment = ref<boolean>(false)

    const scheduleMeetingApiFormRef = ref<ApiFormRefType>(null)

    const scheduleMeetingUrl = new BookingConfirmationSubmitUrlFactory().build(
        localCompany.value,
        localDraftMeeting.value,
        localServiceAggregate.value,
        localizer.getLocale(),
    )

    const localNumberOfRecurringMeetingsToSchedule = ref(
        localDraftMeeting.value.numberOfRecurringMeetingsToSchedule ??
            localServiceAggregate.value.service.recurrenceSettings
                .maximumNumberOfRecurringMeetings,
    )
    const localNumberOfRequestedSeats = ref(
        localDraftMeeting.value.numberOfRequestedSeats ?? 1,
    )

    const handleConfirmationStarted = (
        eventPayload: ConfirmBookMeetingOnStartCallbackParams,
    ) => {
        isProcessingPayment.value = true
    }

    const handleConfirmationFailed = (
        eventPayload: ConfirmBookMeetingOnErrorCallbackParams,
    ) => {
        isProcessingPayment.value = false
        errorMessage.value = null

        if (!eventPayload.message) {
            return
        }

        errorMessage.value = eventPayload.message

        usePageScroll().scrollToTop()
    }

    const getScheduleMeetingApiFormRef = (): NonNullable<ApiFormRefType> => {
        if (!scheduleMeetingApiFormRef.value) {
            handleConfirmationFailed({
                message:
                    'Error trying to confirm meeting, please refresh the page and try again (code 8).',
            })

            throw new Error('Meeting form should not be null.')
        }

        return scheduleMeetingApiFormRef.value
    }

    const canScheduleMeeting = (): boolean => {
        const apiForm = getScheduleMeetingApiFormRef()

        if (apiForm?.isInvalid()) {
            handleConfirmationFailed({ message: null })
            apiForm.reportInvalid()

            return false
        }

        return true
    }

    const handleScheduleMeetingRequested = (
        eventPayload: ConfirmBookMeetingOnScheduleMeetingRequested,
    ) => {
        if (!canScheduleMeeting()) {
            return
        }

        const scheduleMeetingRequest = getScheduleMeetingApiFormRef().submit()

        if (!scheduleMeetingRequest) {
            throw new Error('Meeting form submission should return a request.')
        }

        scheduleMeetingRequest
            .catch((response: ApiFormResponseType) => {
                handleConfirmationFailed({
                    message: response.getMessage() ?? '',
                })
            })
            .finally(() => {
                isProcessingPayment.value = false
            })
    }

    const handleNumberOfRecurringMeetingsToScheduleUpdated = (eventPayload: {
        numberOfRecurringMeetingsToSchedule: number
    }) => {
        localNumberOfRecurringMeetingsToSchedule.value =
            eventPayload.numberOfRecurringMeetingsToSchedule
    }

    const handleNumberOfRequestedSeatsUpdated = (eventPayload: {
        numberOfRequestedSeats: number
    }) => {
        localNumberOfRequestedSeats.value = eventPayload.numberOfRequestedSeats
    }

    // -------------------------
    // Lifecycle hooks
    // -------------------------

    onMounted(() => {
        scrollToDetailsSection()
    })

    // -------------------------
    // Scroll to details section on mobile
    // -------------------------

    const DETAILS_SECTION_ID = 'DETAILS_SECTION_ID'

    const scrollToDetailsSection = (): void => {
        usePageScroll().scrollToElement(DETAILS_SECTION_ID)
    }
</script>
